import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useLang } from "@/context/LanguageContext";
import { ArrowRightLeft, Loader2 } from "lucide-react";

export default function RICTransfer() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [bins, setBins] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ yarn_item_id: "", from_bin_id: "", to_bin_id: "", kg: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { (async () => { setBins(await ric.bins()); setItems(await ric.yarnItems()); })(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    setBusy(true);
    try {
      await ric.transfer({ ...form, kg: parseFloat(form.kg) });
      setMsg(t("Transfer recorded", "ट्रांसफर दर्ज"));
      setForm({ yarn_item_id: "", from_bin_id: "", to_bin_id: "", kg: "" });
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="ric-transfer">
      <h1 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        <ArrowRightLeft size={24} /> {t("Bin transfer", "बिन ट्रांसफर")}
      </h1>
      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="ric-transfer-msg">{msg}</div>}
      <form onSubmit={submit} className="hag-surface p-4 space-y-3">
        <Field label={t("Yarn item", "यार्न")}>
          <select data-testid="ric-tx-item" value={form.yarn_item_id} onChange={(e) => setForm({ ...form, yarn_item_id: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
            <option value="">— {t("select", "चुनें")} —</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.yarn_type} · {i.yarn_count} · {i.colour || "—"} ({i.kg_on_hand} kg)</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("From bin", "किस बिन से")}>
            <select data-testid="ric-tx-from" value={form.from_bin_id} onChange={(e) => setForm({ ...form, from_bin_id: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
              <option value="">—</option>
              {bins.map((b) => <option key={b.id} value={b.id}>{b.bin_number}</option>)}
            </select>
          </Field>
          <Field label={t("To bin", "किस बिन में")}>
            <select data-testid="ric-tx-to" value={form.to_bin_id} onChange={(e) => setForm({ ...form, to_bin_id: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
              <option value="">—</option>
              {bins.map((b) => <option key={b.id} value={b.id}>{b.bin_number}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t("Kg", "कि.")}>
          <input data-testid="ric-tx-kg" type="number" step="0.1" min="0" value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })}
            className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
        </Field>
        <button type="submit" data-testid="ric-tx-submit" disabled={busy}
          className="h-11 px-4 rounded-md text-sm font-semibold text-white flex items-center gap-2"
          style={{ background: "var(--brand-navy)" }}>
          {busy && <Loader2 size={14} className="animate-spin" />} {t("Record transfer", "ट्रांसफर दर्ज करें")}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{label}</div>
      {children}
    </label>
  );
}
